// src/pages/PaymentPage.tsx - COMPLETE UPDATED VERSION

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { paymentAPI, type CourseSelection, type SupportedCurrency } from '../lib/studentAPI';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CreditCard,
  Loader2,
  Wallet,
  Calendar,
  Lock,
  Unlock,
  Clock,
  Trash2,
} from 'lucide-react';

// Currency Selector Component
const CurrencySelector = ({
  currencies,
  selectedCurrency,
  onCurrencyChange,
}: {
  currencies: SupportedCurrency[];
  selectedCurrency: string;
  onCurrencyChange: (code: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selected = currencies.find(c => c.code === selectedCurrency);

  if (currencies.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
      >
        <span className="text-lg">{selected?.flag || '🌍'}</span>
        <span className="font-medium">{selected?.code || 'USD'}</span>
        <span className="text-gray-400">({selected?.symbol})</span>
        <svg className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border z-20 max-h-96 overflow-y-auto">
            <div className="p-2">
              {currencies.map((currency) => (
                <button
                  key={currency.code}
                  onClick={() => {
                    onCurrencyChange(currency.code);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    selectedCurrency === currency.code
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{currency.flag}</span>
                    <div className="text-left">
                      <div className="font-medium">{currency.code}</div>
                      <div className="text-xs text-gray-500">{currency.name}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{currency.symbol}</span>
                    {selectedCurrency === currency.code && (
                      <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Month selection options
const MONTH_OPTIONS = [
  { value: 1, label: '1 month', discount: 0, priceMultiplier: 1 },
  { value: 2, label: '2 months', discount: 1, priceMultiplier: 1.5 },
  { value: 3, label: '3 months', discount: 2, priceMultiplier: 2.85 },
  { value: 6, label: '6 months', discount: 5, priceMultiplier: 5.4 },
];

type PaymentState = 'LOCKED' | 'WAITING_FOR_START' | 'ACTIVE' | 'EXPIRED';

interface ExtendedEnrollment {
  id: string;
  courseId: string;
  studentId: string;
  progress: number;
  isUnlocked: boolean;
  waitingForStartDate: boolean;
  nextBillingDate?: string;
  enrolledAt: string;
  course?: {
    id: string;
    title: string;
    name: string;
    subtitle: string;
    startDate: string;
    coverImage?: string;
  };
  paymentState: PaymentState;
  statusLabel: string;
  statusVariant: 'default' | 'destructive' | 'secondary' | 'outline';
  startDate?: string;
  pricePerMonth?: number;
}

export const PaymentPage = () => {
  const { student } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [enrollments, setEnrollments] = useState<ExtendedEnrollment[]>([]);
  const [selectedSelections, setSelectedSelections] = useState<CourseSelection[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<"success" | "failed" | null>(null);
  
  // Dynamic currency state
  const [currency, setCurrency] = useState("KES");
  const [pricePerMonth, setPricePerMonth] = useState<number | null>(null);
  const [currencySymbol, setCurrencySymbol] = useState<string>("KSh");
  const [supportedCurrencies, setSupportedCurrencies] = useState<SupportedCurrency[]>([]);
  const [usdPerMonth, setUsdPerMonth] = useState<number>(3);
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [clearingPending, setClearingPending] = useState(false);
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [pendingPaymentDetails, setPendingPaymentDetails] = useState<any>(null);
  
  const [summary, setSummary] = useState<{
    totalCourses: number;
    totalMonths: number;
    totalAmount: number;
  }>({ totalCourses: 0, totalMonths: 0, totalAmount: 0 });
   
  useEffect(() => {
  const query = new URLSearchParams(location.search);
  const status = query.get("status");
  const error = query.get("error");

  if (status === "success") {
    setPaymentStatus("success");
    toast.success("Payment completed successfully 🎉");
  }

  if (status === "failed" || error) {
    setPaymentStatus("failed");
    toast.error("Payment failed. Please try again.");
  }
}, [location.search]);

  // Get payment state based on controller rules
  const getPaymentState = (enrollment: any): PaymentState => {
    const now = new Date();
    const startDate = enrollment.course?.startDate ? new Date(enrollment.course.startDate) : null;
    const hasReachedStartDate = startDate ? now >= startDate : true;
    const isUnlocked = enrollment.isUnlocked;
    const nextBillingDate = enrollment.nextBillingDate ? new Date(enrollment.nextBillingDate) : null;
    const isExpired = nextBillingDate && now > nextBillingDate;

    if (startDate && !hasReachedStartDate) {
      return 'WAITING_FOR_START';
    }
    if (isUnlocked && !isExpired) {
      return 'ACTIVE';
    }
    if (isExpired) {
      return 'EXPIRED';
    }
    return 'LOCKED';
  };

  const getStatusLabel = (state: PaymentState, enrollment: any): string => {
    switch (state) {
      case 'LOCKED': return 'Payment Required';
      case 'WAITING_FOR_START': return `Starts ${new Date(enrollment.course?.startDate).toLocaleDateString()}`;
      case 'ACTIVE': return 'Active';
      case 'EXPIRED': return 'Expired - Renew';
      default: return 'Unknown';
    }
  };

  const getStatusVariant = (state: PaymentState): 'default' | 'destructive' | 'secondary' | 'outline' => {
    switch (state) {
      case 'LOCKED': return 'destructive';
      case 'WAITING_FOR_START': return 'secondary';
      case 'ACTIVE': return 'default';
      case 'EXPIRED': return 'destructive';
      default: return 'outline';
    }
  };

  // Load enrollments with current currency
  const loadEnrollments = useCallback(async (currencyCode?: string) => {
    if (!student?.id) return;

    try {
      setLoading(true);
      const activeCurrency = currencyCode || currency;
      
      const res = await paymentAPI.getPaymentStatus(activeCurrency);
      const enrollmentsData = res.data || [];
      
      const enrichedEnrollments: ExtendedEnrollment[] = enrollmentsData.map((enr: any) => {
        const state = getPaymentState(enr);
        return {
          id: enr.id,
          courseId: enr.courseId,
          studentId: enr.studentId,
          progress: enr.progress,
          isUnlocked: enr.isUnlocked,
          waitingForStartDate: enr.waitingForStartDate,
          nextBillingDate: enr.nextBillingDate,
          enrolledAt: enr.enrolledAt,
          course: enr.course,
          paymentState: state,
          statusLabel: getStatusLabel(state, enr),
          statusVariant: getStatusVariant(state),
          startDate: enr.course?.startDate,
          pricePerMonth: enr.pricePerMonth || res.config?.pricePerMonth,
        };
      });

      setEnrollments(enrichedEnrollments);
      setCurrency(res.config?.currency || "USD");
      setPricePerMonth(res.config?.pricePerMonth || null);
      setCurrencySymbol(res.config?.currencySymbol || "$");
      setSupportedCurrencies(res.config?.supportedCurrencies || []);
      setUsdPerMonth(res.config?.usdPerMonth || 3);

      const payableEnrollments = enrichedEnrollments
        .filter(e => e.paymentState !== 'ACTIVE')
        .map(e => ({ courseId: e.courseId, months: 1 }));
      
      setSelectedSelections(payableEnrollments);
      updateSummary(payableEnrollments, res.config?.pricePerMonth || 0);

    } catch (err: any) {
      toast.error(err.message || "Failed to load payment data");
    } finally {
      setLoading(false);
    }
  }, [student, currency]);

  // Handle currency change
  const handleCurrencyChange = async (newCurrency: string) => {
    if (newCurrency === currency) return;
    setCurrency(newCurrency);
    await loadEnrollments(newCurrency);
  };

  const updateSummary = (selections: CourseSelection[], pricePerMonthValue: number) => {
    const totalCourses = selections.length;
    const totalMonths = selections.reduce((sum, s) => sum + s.months, 0);
    const totalAmount = selections.reduce((sum, s) => sum + (s.months * pricePerMonthValue), 0);
    setSummary({ totalCourses, totalMonths, totalAmount });
  };

  const toggleCourse = (courseId: string) => {
    const existing = selectedSelections.find(s => s.courseId === courseId);
    
    if (existing) {
      const updated = selectedSelections.filter(s => s.courseId !== courseId);
      setSelectedSelections(updated);
      updateSummary(updated, pricePerMonth || 0);
    } else {
      const updated = [...selectedSelections, { courseId, months: 1 }];
      setSelectedSelections(updated);
      updateSummary(updated, pricePerMonth || 0);
    }
  };

  const updateMonths = (courseId: string, months: number) => {
    const updated = selectedSelections.map(s =>
      s.courseId === courseId ? { ...s, months } : s
    );
    setSelectedSelections(updated);
    updateSummary(updated, pricePerMonth || 0);
  };

  const getSelectedMonths = (courseId: string): number => {
    return selectedSelections.find(s => s.courseId === courseId)?.months || 1;
  };

  const isSelected = (courseId: string): boolean => {
    return selectedSelections.some(s => s.courseId === courseId);
  };

  const getTotalAmount = (): number => {
    if (!pricePerMonth) return 0;
    return selectedSelections.reduce((sum, s) => sum + (s.months * pricePerMonth), 0);
  };

  const getPriceDisplay = (months: number): string => {
    if (!pricePerMonth) return '...';
    const total = months * pricePerMonth;
    return `${currencySymbol} ${total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const getDiscountLabel = (months: number): string | null => {
    const option = MONTH_OPTIONS.find(o => o.value === months);
    return option?.discount ? `${option.discount}% off` : null;
  };

  // Clear all stale pending payments
  const handleClearAllPending = async () => {
    setClearingPending(true);
    try {
      const result = await paymentAPI.clearPendingPayments(0); // Clear ALL pending regardless of age
      if (result.cleared > 0) {
        toast.success(`Cleared ${result.cleared} pending payment(s). Reloading...`);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.info("No pending payments found to clear");
      }
    } catch (error: any) {
      console.error("Clear all pending error:", error);
      toast.error(error.response?.data?.message || "Failed to clear pending payments");
    } finally {
      setClearingPending(false);
    }
  };

  // Clear specific pending payment and retry
  const handleClearPendingAndRetry = async () => {
    if (!pendingPaymentDetails?.id) return;
    
    setClearingPending(true);
    try {
      const result = await paymentAPI.clearSpecificPendingPayment(pendingPaymentDetails.id);
      if (result.success) {
        toast.success("Pending payment cleared. Retrying...");
        setShowPendingDialog(false);
        setPendingPaymentDetails(null);
        setTimeout(() => handlePayment(), 500);
      } else {
        toast.error(result.message || "Failed to clear pending payment");
      }
    } catch (error: any) {
      console.error("Clear pending error:", error);
      toast.error(error.response?.data?.message || "Failed to clear pending payment");
    } finally {
      setClearingPending(false);
    }
  };

  const handlePayment = async () => {
    if (selectedSelections.length === 0) {
      toast.error("Select at least one course");
      return;
    }

    if (processing) return; // prevent double clicks
    setProcessing(true);

    try {
      const res = await paymentAPI.initiatePayment({
        courseSelections: selectedSelections,
        currency,
      });

      // ============================================================
      // CASE 1: NEW PAYMENT → REDIRECT
      // ============================================================
      if (res?.redirectUrl) {
        window.location.href = res.redirectUrl;
        return;
      }

      // ============================================================
      // CASE 2: REUSE EXISTING PAYMENT
      // ============================================================
      if (res?.existingPayment) {
        const p = res.existingPayment;

        toast.warning(
          `You already have a pending payment of ${p.currency} ${p.amount?.toLocaleString()}`,
          {
            duration: 12000,
            action: {
              label: "Continue",
              onClick: () => {
                if (res.redirectUrl) {
                  window.location.href = res.redirectUrl;
                }
              },
            },
          }
        );

        setPendingPaymentDetails({
          id: p.id,
          amount: p.amount,
          currency: p.currency,
          createdAt: p.createdAt,
          expiresInMinutes: 30,
        });

        setShowPendingDialog(true);
        return;
      }

      // ============================================================
      // FALLBACK (NO VALID RESPONSE)
      // ============================================================
      toast.error("Failed to start payment");
    } catch (err: any) {
      console.error("Payment error:", err);

      const status = err?.response?.status || err?.status;

      // ============================================================
      // NETWORK ERROR
      // ============================================================
      if (err?.code === "ERR_NETWORK") {
        toast.error("Network error. Please try again.");
        return;
      }

      // ============================================================
      // UNAUTHORIZED
      // ============================================================
      if (status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/login");
        return;
      }

      // ============================================================
      // BAD REQUEST
      // ============================================================
      if (status === 400) {
        toast.error(err.response?.data?.message || "Invalid request");
        return;
      }

      // ============================================================
      // GENERIC ERROR
      // ============================================================
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          "Payment failed. Please try again."
      );
    } finally {
      setProcessing(false);
    }
  };

    
  // Check for existing pending payments on page load
  const checkForPendingPayments = async () => {
    try {
      const pendingStatus = await paymentAPI.getPendingPayments();
      if (pendingStatus.hasPending && pendingStatus.recentPending.length > 0) {
        const pending = pendingStatus.recentPending[0];
        toast.warning(
          `You have a pending payment of ${pending.currency} ${pending.amount?.toLocaleString()}`,
          {
            duration: 10000,
            action: {
              label: "Clear It",
              onClick: async () => {
                await paymentAPI.clearSpecificPendingPayment(pending.id);
                toast.success("Pending payment cleared");
                loadEnrollments();
              },
            },
          }
        );
      }
    } catch (error) {
      console.error("Failed to check pending payments:", error);
    }
  };

  // Handle URL state for pre-selected course
  useEffect(() => {
    const state = location.state as any;
    if (state?.selectedEnrollmentId && enrollments.length > 0) {
      const match = enrollments.find(e => e.id === state.selectedEnrollmentId);
      if (match && match.paymentState !== 'ACTIVE') {
        setSelectedSelections(prev => {
          const exists = prev.some(s => s.courseId === match.courseId);
          if (!exists) {
            const updated = [...prev, { courseId: match.courseId, months: 1 }];
            updateSummary(updated, pricePerMonth || 0);
            return updated;
          }
          return prev;
        });
      }
    }
  }, [location.state, enrollments, pricePerMonth]);

  // Initial load
  useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  // Check for pending payments on mount
  useEffect(() => {
    if (student?.id) {
      checkForPendingPayments();
    }
  }, [student?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
      </div>
    );
  }

  // Group enrollments by payment state
  const lockedEnrollments = enrollments.filter(e => e.paymentState === 'LOCKED');
  const waitingEnrollments = enrollments.filter(e => e.paymentState === 'WAITING_FOR_START');
  const activeEnrollments = enrollments.filter(e => e.paymentState === 'ACTIVE');
  const expiredEnrollments = enrollments.filter(e => e.paymentState === 'EXPIRED');

  // Calculate USD equivalent for display
  const getUsdEquivalent = (amount: number): string => {
    if (currency === 'USD') return '';
    const currentCurrencyRate = supportedCurrencies.find(c => c.code === currency)?.rateToUSD || 130;
    const usdAmount = amount / currentCurrencyRate;
    return `≈ $${usdAmount.toFixed(2)} USD`;
  };
 
  return (
    <div className="min-h-screen bg-gray-50">

      {paymentStatus === "success" && (
        <div className="bg-green-100 text-green-700 p-3 rounded-lg mb-4">
          🎉 Payment successful! Your courses are now active.
        </div>
      )}

      {paymentStatus === "failed" && (
        <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4">
          ❌ Payment failed. Please try again or retry checkout.
        </div>
      )}

      <header className="bg-white border-b px-6 py-4 flex justify-between items-center">
        <Button variant="ghost" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        
        <div className="flex items-center gap-3">
          {/* Clear Pending Payments Button */}
          <button
            onClick={handleClearAllPending}
            disabled={clearingPending}
            className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 transition-colors disabled:opacity-50"
          >
            {clearingPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Clear Pending payments
          </button>
          
          {/* Currency Selector */}
          {supportedCurrencies.length > 0 && (
            <CurrencySelector
              currencies={supportedCurrencies}
              selectedCurrency={currency}
              onCurrencyChange={handleCurrencyChange}
            />
          )}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-12">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold">Manage Payments</h1>
            <p className="text-gray-600 mt-2">
              ${usdPerMonth} USD/month per course • {currencySymbol} {pricePerMonth?.toLocaleString()}/{currency}/month
            </p>
            <p className="text-sm text-blue-600 mt-1">
              💡 Make your payment to ensure you don't miss out
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {/* Main Content - Course Selection */}
            <div className="md:col-span-2 space-y-6">
              
              {/* LOCKED - Need Payment */}
              {lockedEnrollments.length > 0 && (
                <div className="bg-white p-6 rounded-2xl border">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-red-600" />
                    Payment Required
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">
                    These courses have started and need payment to continue learning
                  </p>

                  <div className="space-y-4">
                    {lockedEnrollments.map((enr) => (
                      <div
                        key={enr.id}
                        className={`p-4 rounded-xl border transition-all ${
                          isSelected(enr.courseId)
                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex flex-wrap gap-4 items-start justify-between">
                          <div className="flex gap-3 flex-1">
                            <Checkbox
                              checked={isSelected(enr.courseId)}
                              onCheckedChange={() => toggleCourse(enr.courseId)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <p className="font-semibold text-lg">
                                {enr.course?.title || enr.course?.name || "Course"}
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                {enr.course?.subtitle}
                              </p>
                              <Badge variant="destructive" className="mt-2">
                                Locked - Payment Required
                              </Badge>
                            </div>
                          </div>

                          {isSelected(enr.courseId) && (
                            <div className="w-48">
                              <label className="text-xs text-gray-500 block mb-1">
                                Select duration
                              </label>
                              <select
                                value={getSelectedMonths(enr.courseId)}
                                onChange={(e) => updateMonths(enr.courseId, parseInt(e.target.value))}
                                className="w-full p-2 border rounded-lg text-sm bg-white"
                              >
                                {MONTH_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label} - {getPriceDisplay(opt.value)}
                                  </option>
                                ))}
                              </select>
                              {getDiscountLabel(getSelectedMonths(enr.courseId)) && (
                                <p className="text-xs text-green-600 mt-1">
                                  {getDiscountLabel(getSelectedMonths(enr.courseId))}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* WAITING FOR START DATE */}
              {waitingEnrollments.length > 0 && (
                <div className="bg-blue-50 p-6 rounded-2xl border border-blue-200">
                  <h2 className="text-lg font-semibold mb-4 text-blue-800 flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Upcoming Courses - Pay to Access Learning Bay
                  </h2>
                  <p className="text-sm text-blue-600 mb-4">
                    💡 These courses haven't started yet. You can pay now and they'll wait for the cohort to start
                  </p>
                  <div className="space-y-4">
                    {waitingEnrollments.map((enr) => (
                      <div
                        key={enr.id}
                        className={`p-4 rounded-xl border transition-all ${
                          isSelected(enr.courseId)
                            ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-200'
                            : 'bg-white border-blue-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex flex-wrap gap-4 items-start justify-between">
                          <div className="flex gap-3 flex-1">
                            <Checkbox
                              checked={isSelected(enr.courseId)}
                              onCheckedChange={() => toggleCourse(enr.courseId)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <p className="font-semibold text-lg">
                                {enr.course?.title || enr.course?.name || "Course"}
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                {enr.course?.subtitle}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Clock className="h-4 w-4 text-blue-600" />
                                <span className="text-sm text-blue-600">
                                  Starts {enr.startDate ? new Date(enr.startDate).toLocaleDateString() : 'TBD'}
                                </span>
                              </div>
                              <Badge variant="secondary" className="mt-2">
                                Pre-order - Pay now
                              </Badge>
                            </div>
                          </div>

                          {isSelected(enr.courseId) && (
                            <div className="w-48">
                              <label className="text-xs text-gray-500 block mb-1">
                                Select duration
                              </label>
                              <select
                                value={getSelectedMonths(enr.courseId)}
                                onChange={(e) => updateMonths(enr.courseId, parseInt(e.target.value))}
                                className="w-full p-2 border rounded-lg text-sm bg-white"
                              >
                                {MONTH_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label} - {getPriceDisplay(opt.value)}
                                  </option>
                                ))}
                              </select>
                              {getDiscountLabel(getSelectedMonths(enr.courseId)) && (
                                <p className="text-xs text-green-600 mt-1">
                                  {getDiscountLabel(getSelectedMonths(enr.courseId))}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ACTIVE COURSES */}
              {activeEnrollments.length > 0 && (
                <div className="bg-green-50 p-6 rounded-2xl border border-green-200">
                  <h2 className="text-lg font-semibold mb-4 text-green-800 flex items-center gap-2">
                    <Unlock className="h-5 w-5" />
                    Active Courses
                  </h2>
                  <p className="text-sm text-green-600 mb-4">
                    These courses are already unlocked and accessible.
                  </p>
                  <div className="space-y-3">
                    {activeEnrollments.map((enr) => (
                      <div key={enr.id} className="p-3 bg-white rounded-lg border border-green-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{enr.course?.title || "Course"}</p>
                            <p className="text-sm text-green-600 mt-1">
                              Active • Access granted
                            </p>
                            {enr.nextBillingDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                Next billing: {new Date(enr.nextBillingDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <Badge variant="default">Active</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* EXPIRED COURSES */}
              {expiredEnrollments.length > 0 && (
                <div className="bg-orange-50 p-6 rounded-2xl border border-orange-200">
                  <h2 className="text-lg font-semibold mb-4 text-orange-800 flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    Expired Subscriptions (Renew to Continue)
                  </h2>
                  <p className="text-sm text-orange-600 mb-4">
                    Your subscription has expired. Renew to continue accessing these courses.
                  </p>
                  <div className="space-y-4">
                    {expiredEnrollments.map((enr) => (
                      <div
                        key={enr.id}
                        className={`p-4 rounded-xl border transition-all ${
                          isSelected(enr.courseId)
                            ? 'border-orange-500 bg-orange-100 ring-2 ring-orange-200'
                            : 'bg-white border-orange-200 hover:border-orange-300'
                        }`}
                      >
                        <div className="flex flex-wrap gap-4 items-start justify-between">
                          <div className="flex gap-3 flex-1">
                            <Checkbox
                              checked={isSelected(enr.courseId)}
                              onCheckedChange={() => toggleCourse(enr.courseId)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <p className="font-semibold text-lg">
                                {enr.course?.title || enr.course?.name || "Course"}
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                {enr.course?.subtitle}
                              </p>
                              <Badge variant="destructive" className="mt-2">
                                Expired - Renew to continue
                              </Badge>
                            </div>
                          </div>

                          {isSelected(enr.courseId) && (
                            <div className="w-48">
                              <label className="text-xs text-gray-500 block mb-1">
                                Select duration
                              </label>
                              <select
                                value={getSelectedMonths(enr.courseId)}
                                onChange={(e) => updateMonths(enr.courseId, parseInt(e.target.value))}
                                className="w-full p-2 border rounded-lg text-sm bg-white"
                              >
                                {MONTH_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label} - {getPriceDisplay(opt.value)}
                                  </option>
                                ))}
                              </select>
                              {getDiscountLabel(getSelectedMonths(enr.courseId)) && (
                                <p className="text-xs text-green-600 mt-1">
                                  {getDiscountLabel(getSelectedMonths(enr.courseId))}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {enrollments.length === 0 && (
                <div className="bg-white p-8 rounded-2xl border text-center">
                  <p className="text-gray-500">You haven't enrolled in any courses yet</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => navigate('/dashboard')}
                  >
                    Browse Courses
                  </Button>
                </div>
              )}
            </div>

            {/* Sidebar - Payment Summary */}
            <div className="md:col-span-1">
              <div className="bg-white p-6 rounded-2xl border sticky top-6">
                <h3 className="text-lg font-semibold mb-4">Payment Summary</h3>
                
                <div className="space-y-3 border-b pb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Courses selected:</span>
                    <span className="font-medium">{summary.totalCourses}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total subscription months:</span>
                    <span className="font-medium">{summary.totalMonths}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Monthly rate:</span>
                    <span>{currencySymbol} {pricePerMonth?.toLocaleString()}</span>
                  </div>
                </div>

                <div className="pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total to pay:</span>
                    <span>{currencySymbol} {getTotalAmount().toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                  </div>
                  {currency !== 'USD' && getTotalAmount() > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {getUsdEquivalent(getTotalAmount())}
                    </p>
                  )}
                </div>

                <Button
                  onClick={handlePayment}
                  disabled={processing || selectedSelections.length === 0}
                  className="w-full mt-6 h-12 bg-blue-600 hover:bg-blue-700"
                >
                  {processing ? (
                    <>
                      <Loader2 className="animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2" />
                      Pay {currencySymbol} {getTotalAmount().toLocaleString()}
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-400 text-center mt-4">
                  Secure payment via Pesapal<br />
                  Ensure continuity of learning. Hurry up to skillify
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Pending Payment Dialog */}
      {showPendingDialog && pendingPaymentDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-xl font-bold mb-4">Pending Payment Found</h3>
            <div className="space-y-3 mb-6">
              <p className="text-gray-600">
                You have an existing pending payment that hasn't been completed.
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm">
                  <span className="font-medium">Amount:</span>{' '}
                  {pendingPaymentDetails.currency} {pendingPaymentDetails.amount?.toLocaleString()}
                </p>
                <p className="text-sm mt-1">
                  <span className="font-medium">Created:</span>{' '}
                  {new Date(pendingPaymentDetails.createdAt).toLocaleString()}
                </p>
                <p className="text-sm mt-1">
                  <span className="font-medium">Expires in:</span>{' '}
                  {pendingPaymentDetails.expiresInMinutes} minutes
                </p>
              </div>
              <p className="text-sm text-amber-600">
                ⚠️ You can only have one pending payment at a time. Clear the existing payment to create a new one.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPendingDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClearPendingAndRetry}
                disabled={clearingPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {clearingPending ? (
                  <>
                    <Loader2 className="inline animate-spin mr-2 h-4 w-4" />
                    Clearing...
                  </>
                ) : (
                  'Clear & Retry'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
